import React, { useState, useEffect } from 'react';
import { Card, Space, Tag, Spin, Alert, Empty, Divider, Typography, Button } from 'antd';
import { RobotOutlined, PictureOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { characterApi } from '@/services/api/character';
import type { AvatarCharacter } from '@/services/api/character';

const { Title, Text } = Typography;

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

interface Expression {
  key: string;
  name: string;
  file: string;
  emotion: string;
}

interface CharacterMetadata {
  id: string;
  name: string;
  description: string;
  version: string;
  defaultExpression: string;
  expressions: Expression[];
}

// 表情图标映射（常量，不需要 useMemo）
const EXPRESSION_ICONS: Record<string, string> = {
  happy: '😊',
  angry: '😠',
  sad: '😢',
  surprised: '😲',
  shy: '😊',
  confused: '🤔',
  scared: '😨',
  tsundere: '😒',
  excited: '🤩',
  sleepy: '😴',
  playful: '😜',
  neutral: '😐',
  normal: '😐',
  smile: '🙂',
  laugh: '😆',
  teary: '😭',
  thinking: '🤔',
  thinking_tilt: '🤔',
  idle: '😶',
};

const EMOTION_COLORS: Record<string, string> = {
  positive: 'success',
  negative: 'error',
  neutral: 'default',
};

// 表情卡片组件
const ExpressionCard: React.FC<{
  expr: Expression;
  characterId: string;
}> = ({ expr, characterId }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const imageUrl = `${API_BASE}/avatars/${characterId}/${expr.file}`;

  if (error) {
    return (
      <Card
        size="small"
        title={
          <Space>
            <span style={{ fontSize: 20 }}>{EXPRESSION_ICONS[expr.key] || '😐'}</span>
            <span>{expr.name}</span>
          </Space>
        }
        extra={
          <Tag color={EMOTION_COLORS[expr.emotion] || 'default'}>
            {expr.emotion}
          </Tag>
        }
        style={{ textAlign: 'center' }}
      >
        <div style={{
          width: '100%',
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff5f5',
          color: '#999',
          fontSize: 12,
          borderRadius: 8,
        }}>
          暂无图片
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
          {expr.file}
        </div>
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={
        <Space>
          <span style={{ fontSize: 20 }}>{EXPRESSION_ICONS[expr.key] || '😐'}</span>
          <span>{expr.name}</span>
        </Space>
      }
      extra={
        <Tag color={EMOTION_COLORS[expr.emotion] || 'default'}>
          {expr.emotion}
        </Tag>
      }
      style={{ textAlign: 'center' }}
    >
      <div style={{
        width: '100%',
        height: 200,
        borderRadius: 8,
        overflow: 'hidden',
        background: '#f5f5f5',
        position: 'relative',
      }}>
        {!loaded && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Spin />
          </div>
        )}
        <img
          src={imageUrl}
          alt={expr.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: loaded ? 'block' : 'none',
            cursor: 'pointer',
          }}
          onLoad={() => setLoaded(true)}
          onError={() => {
            setLoaded(true);
            setError(true);
          }}
          onClick={() => {
            window.open(imageUrl, '_blank');
          }}
        />
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
        {expr.file}
      </div>
    </Card>
  );
};

const CharacterPreview: React.FC = () => {
  const { characterId } = useParams<{ characterId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState<CharacterMetadata | null>(null);
  const [character, setCharacter] = useState<AvatarCharacter | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!characterId) return;

      setLoading(true);
      setError('');

      try {
        // 加载角色详情
        const charRes = await characterApi.getDetail(characterId);
        if (cancelled) return;

        if (charRes.code === 200) {
          setCharacter(charRes.data);
        }

        // 加载元数据
        const metadataUrl = `${API_BASE}/avatars/${characterId}/metadata.json`;
        const response = await fetch(metadataUrl);
        if (cancelled) return;

        if (response.ok) {
          const data = await response.json();
          setMetadata(data);
        } else {
          setError('无法加载角色元数据，请确保角色已生成完成');
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('加载角色数据失败:', err);
        setError('加载角色数据失败：' + (err.message || '未知错误'));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [characterId]);

  // 过滤掉 blink 图片
  const filteredExpressions = metadata?.expressions?.filter(
    expr => !expr.key.includes('_blink')
  ) || [];

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" tip="加载角色数据..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="加载失败"
          description={error}
          type="error"
          showIcon
        />
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Space>
            <Button onClick={() => navigate('/character/list')}>返回列表</Button>
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              重新加载
            </Button>
          </Space>
        </div>
      </div>
    );
  }

  if (!metadata) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Empty description="暂无角色数据" />
        <div style={{ marginTop: 24 }}>
          <Button onClick={() => navigate('/character/list')}>返回列表</Button>
        </div>
      </div>
    );
  }

  // 使用 characterId 作为 key，强制在角色变化时重新挂载整个组件
  return (
    <div key={characterId} style={{ padding: '24px', minHeight: '100vh' }}>
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>{metadata.name || character?.name}</span>
          </Space>
        }
        variant="bordered"
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
        extra={
          <Button onClick={() => navigate('/character/list')}>返回列表</Button>
        }
      >
        {/* 角色信息 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>角色信息</Title>
          <div style={{ marginBottom: 8 }}>
            <Text strong>角色 ID：</Text>
            <Text code>{metadata.id}</Text>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text strong>角色名称：</Text>
            <Text>{metadata.name || character?.name}</Text>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text strong>外貌描述：</Text>
            <Text>{metadata.description || character?.description}</Text>
          </div>
          <div>
            <Text strong>状态：</Text>
            {character?.status === 'completed' ? (
              <Tag icon={<PictureOutlined />} color="success">已完成</Tag>
            ) : character?.status === 'generating' ? (
              <Tag icon={<PictureOutlined />} color="processing">生成中</Tag>
            ) : (
              <Tag icon={<PictureOutlined />} color="default">{character?.status || '未知'}</Tag>
            )}
          </div>
        </div>

        <Divider />

        {/* 表情预览 */}
        <div>
          <Title level={4}>表情预览</Title>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 16,
            }}
          >
            {filteredExpressions.map((expr) => (
              <ExpressionCard
                key={expr.key}
                expr={expr}
                characterId={characterId!}
              />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CharacterPreview;
